<template>
  <div v-if="shouldShowRanking">
    <template v-if="isKonkatsuPage">
      <div class="commonMargin rankingTitleContainer">
        <div class="titlePulldownRow">
          <h2 class="commonKonkatsuHeading">{{ title }}</h2>
          <div v-if="showPulldownMenu" class="prefectureSelect btn-pulldown-wrap">
            <select v-model="currentPrefectureSlug" class="btn-pulldown" aria-label="ランキングの都道府県" @change="fetchRankingEvents">
              <option v-for="(pref, key) in prefectures" :key="key" :value="pref.slug">
                {{ pref.name }}
              </option>
            </select>
            <icon :size="2" class="btn-pulldown-icon" fas="fa-caret-down" />
          </div>
        </div>
      </div>
    </template>
    <h2 v-else class="commonHeadingBlack paddingB3 rankingTitleContainer">
      <div v-if="showPulldownMenu" class="prefectureSelect btn-pulldown-wrap">
        <select v-model="currentPrefectureSlug" class="btn-pulldown" aria-label="ランキングの都道府県" @change="fetchRankingEvents">
          <option v-for="(pref, key) in prefectures" :key="key" :value="pref.slug">
            {{ pref.name }}
          </option>
        </select>
        <icon :size="2" class="btn-pulldown-icon" fas="fa-caret-down" />
      </div>
      {{ title }}
    </h2>
    <div class="contentBox" :class="{ commonMargin: isKonkatsuPage }">
      <div v-if="isLoadingRanking" class="loadingContainer alignCenter">
        <icon :size="4" fas="fa-spinner" class="fa-spin" />
      </div>
      <template v-else-if="rankingEvents && rankingEvents.length > 0">
        <div class="eventRankingList">
          <div v-for="(event, index) in rankingEvents.slice(0, 3)" :key="index" class="eventRankingList-item" @click="opixTopSpRanking(event)">
            <div>
              <client-only>
                <img v-if="index < 3" :src="rankTopIcon(event)" alt="Machicon Ranking" />
              </client-only>
              <span class="eventRankingList-item-rankingLabel">{{ event.rank }}位</span>
            </div>
            <event-list-item :event="event" :is-first-event="index === 0" />
          </div>
        </div>
        <custom-nuxt-link v-if="showMoreRankingLink" :to="moreRankingPath" class="commonButton marginT4 btn-secondary-40">
          <span class="marginR2">4位〜20位を見る</span>
        </custom-nuxt-link>
      </template>
      <p v-else class="paddingT4">該当エリアのランキングがありません。</p>
    </div>
    <custom-nuxt-link v-if="showMoreRankingLinkForPC" :to="moreRankingPath" class="commonButton marginT4 btn-secondary-40">
      <span class="marginR2">4位〜20位を見る</span>
    </custom-nuxt-link>
  </div>
</template>
<script>
import EventModel from '@/models/events'
import { listPrefectures } from '@/models/places'
import { numberToCurrency, timeConverter } from '@/utils/converter'
import MjSelectMixin from '@/utils/mixins/MjSelectMixin'
import TicketMixin from '@/utils/mixins/TicketMixin'
import { buildQueryString } from '@/utils/url-action'
const EventListItem = defineAsyncComponent(() => import('@/components/search/EventListItem.vue'))

const DEFAULT_PREFECTURE_SLUG = 'tokyo'
// ランキングは3位までしか表示しないが、4位〜20位を見るボタン表示判断のため4位まで取得
const DEFAULT_LIMIT_EVENT = 4
const SHOWABLE_EVENT_COUNT = 3
const VALID_CATEGORIES = ['konkatsu', 'party', 'interest', '']
export default {
  components: { EventListItem },
  mixins: [MjSelectMixin, TicketMixin],
  props: {
    prefecture: { type: Object, default: null, required: false },
    areaGroup: { type: Object, default: null },
    area: { type: Object, default: null },
    isKonkatsuPage: { type: Boolean, default: false, required: false },
    category: {
      type: String,
      default: 'konkatsu',
      validator(value) {
        return VALID_CATEGORIES.includes(value)
      }
    }
  },
  emits: ['click-top-sp-ranking'],
  data() {
    return {
      currentPrefectureSlug: null,
      rankingEvents: [],
      isLoadingRanking: true,
      pageDetector: null
    }
  },
  computed: {
    moreRankingPath() {
      switch (this.pageType) {
        case 'konkatsu':
          return {
            name: 'prefecture-rankings-categories',
            params: {
              prefecture: this.currentPrefectureSlug,
              category: 'konkatsu'
            }
          }
        case 'area-category':
        case 'area': {
          const isAreaPage = this.pageType === 'area'
          const categoryParams = isAreaPage ? {} : { category: this.category }
          const rankingPathName = placeType => {
            return isAreaPage ? `${placeType}_rankings-${placeType}` : `${placeType}-rankings-categories`
          }
          if (this.area) {
            return {
              name: rankingPathName('area'),
              params: {
                area: this.area.slug,
                ...categoryParams
              }
            }
          } else if (this.areaGroup) {
            return {
              name: rankingPathName('area_group'),
              params: {
                area_group: this.areaGroup.slug,
                ...categoryParams
              }
            }
          } else {
            return {
              name: rankingPathName('prefecture'),
              params: {
                prefecture: this.prefectureSlug,
                ...categoryParams
              }
            }
          }
        }
        default:
          return {
            name: 'prefecture_rankings-prefecture',
            params: {
              prefecture: this.currentPrefectureSlug
            }
          }
      }
    },
    prefectures() {
      return listPrefectures().filter(prefecture => prefecture.slug !== 'other')
    },
    rankingFourthEvent() {
      return this.rankingEvents[3]
    },
    pageType() {
      if (this.isKonkatsuPage) return 'konkatsu'
      if (this.category === '') return 'area'
      if (this.pageDetector.isAreasCategoriesPage || this.pageDetector.isAreasInterestsPage) return 'area-category'
      return 'top'
    },
    title() {
      switch (this.pageType) {
        case 'konkatsu':
          return '婚活パーティランキング'

        case 'area-category':
        case 'area': {
          const areaName = [this.area?.name || this.areaGroup?.name, this.prefectureName].filter(v => v).join('/')
          const categoryName = this.category === 'konkatsu' ? '婚活パーティー' : EventModel.getEventTypeName(this.category) || '街コン'
          return `人気${categoryName}ランキング（${areaName}）`
        }

        default: {
          if (!this.$store.isLoggedIn) return '人気イベントランキング'
          return `${this.prefectureName}の人気イベントランキング`
        }
      }
    },
    showPulldownMenu() {
      switch (this.pageType) {
        case 'area-category':
        case 'area':
          return false
        default:
          return !this.$store.isLoggedIn
      }
    },
    prefectureSlug() {
      return this.prefecture?.slug || this.prefecture?.prefecture_slug || DEFAULT_PREFECTURE_SLUG
    },
    prefectureName() {
      return this.prefecture?.name || this.prefecture?.prefecture_name || this.prefectures.find(v => v.slug === DEFAULT_PREFECTURE_SLUG).name
    },
    showMoreRankingLink() {
      return (!this.$store.isPC || this.isKonkatsuPage) && this.rankingEvents.length > SHOWABLE_EVENT_COUNT
    },
    showMoreRankingLinkForPC() {
      return this.$store.isPC && !this.isKonkatsuPage && this.rankingEvents.length > SHOWABLE_EVENT_COUNT
    },
    shouldShowRanking() {
      if (this.pageType !== 'area-category') return true
      return this.rankingEvents.length < SHOWABLE_EVENT_COUNT ? false : true
    }
  },
  created() {
    this.currentPrefectureSlug = this.prefectureSlug
    this.pageDetector = new PageDetector(this.$route)

    this.fetchRankingEvents()
  },
  methods: {
    numberToCurrency,
    fetchRankingEvents() {
      this.fetchRankingEventsWithLimit(DEFAULT_LIMIT_EVENT)
    },
    rankTopIcon(event) {
      return new URL(`/assets/images/svg/rank-${event.rank}.svg`, import.meta.url).href
    },
    startAtFormat(startAt) {
      const timeConverterObj = timeConverter(startAt)
      return `${timeConverterObj.fullDateTimeSingleHourWithoutYear}〜`
    },
    femaleTicket(event) {
      return event.tickets && event.tickets.female
    },
    maleTicket(event) {
      return event.tickets && event.tickets.male
    },
    areaName(event) {
      return event.area ? event.area.name : ''
    },
    eventLasts(events) {
      const last6Events = []
      for (let i = 4; i < events.length; i += 2) {
        last6Events.push(events.slice(i, i + 2))
      }
      return last6Events
    },
    eventItemClassName(length, index) {
      if (length <= 1) return 'singleEvent'
      if (index === 0) return 'evenEvent'
      return 'oddEvent'
    },
    opixTopSpRanking(event) {
      const data = {
        path: `/events/${event.code}/`,
        showedBlock: 'top sp ranking'
      }
      this.$emit('click-top-sp-ranking', data)
    },
    fetchRankingEventsWithLimit(limit) {
      const query = {
        limit: limit
      }
      switch (this.pageType) {
        case 'konkatsu':
          query.event_type = 'konkatsu'
          break
        case 'area-category':
          query.event_type = this.category
          break
        case 'area':
          break
      }

      let apiUrl = ''
      switch (this.pageType) {
        case 'area-category':
        case 'area': {
          const basePath = this.area
            ? `ranking/areas/${this.area.slug}`
            : this.areaGroup
              ? `ranking/area_groups/${this.areaGroup.slug}`
              : `ranking/prefectures/${this.prefectureSlug}`

          apiUrl = `${basePath}/${buildQueryString(query)}`
          break
        }
        default:
          apiUrl = `ranking/prefectures/${this.currentPrefectureSlug}/${buildQueryString(query)}`
      }

      this.$store
        .dispatch('apiGetV2', apiUrl)
        .then(response => {
          this.rankingEvents = response.data.ranking
        })
        .catch(e => {
          const errorCode = parseInt(e.response && e.response.status)
          if (errorCode === 404) return []
        })
        .finally(() => {
          this.isLoadingRanking = false
        })
    },
    minPriceTicket(ticket) {
      if (parseInt(ticket.min_price) >= 0) {
        return `${numberToCurrency(ticket.min_price)}${ticket.has_ticket_price_from ? '〜' : ''}`
      }
      return ''
    }
  }
}
</script>
<style lang="scss" scoped>
.contentBox {
  background: $white;
  padding: 1.6rem 1rem;
  border-radius: 3px;

  @include pc {
    margin: 1.6rem;
  }

  .loadingContainer {
    padding-top: 25%;
    height: 600px;
  }
}

.eventRankingList {
  @include pc {
    display: flex;
    flex-direction: row;
    gap: 1rem;
  }

  &-item {
    margin-bottom: 1.5rem;

    @include pc {
      margin-bottom: 0;
      width: calc((100% - 3rem) / 3);
    }

    &:not(:last-child) {
      border-bottom: 1px solid $gray-c6;

      @include pc {
        border: 0;
      }
    }

    &:last-child {
      margin-bottom: -0.5rem;
    }

    &.itemLast {
      border-bottom: 1px solid $gray-c6;
      margin-bottom: 1rem;
    }

    &-img {
      max-height: 180px;
      border: 1px solid $gray-c6;

      &.fixedDimension {
        width: 100%;
        height: auto;
        object-fit: scale-down;
      }
    }

    &-rankingLabel {
      position: relative;
      top: -3px;
      color: $black-1a;
      font-size: 1.6rem;
      font-weight: bold;
    }

    &-area {
      @include textEllipsis;
      display: block;
      float: left;
      min-width: 60px;
      max-width: 100px;
      padding: 0.1rem 0.6rem;
      margin-right: 0.6rem;
      border-radius: 3px;
      color: $white;
      font-size: 1.2rem;
      text-align: center;
      background-color: $gray-55;
    }

    &-datetime {
      font-weight: bold;
      font-size: 1.2rem;
      color: $black-1a;
    }

    :deep(.eventItem) {
      box-shadow: 0 0;
      padding: 0;

      .eventItem-head {
        padding: 0 1rem;
      }
    }
  }
}

.rankingTitleContainer {
  position: relative;
}

.prefectureSelect {
  position: absolute;
  right: 0;
}

.titlePulldownRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4rem;
  margin-bottom: 1rem;

  h2.commonKonkatsuHeading {
    margin: 0;
    padding: 0;
    flex: 1;
  }

  @include pc {
    justify-content: center;
    position: relative;
  }
}

.titlePulldownRow {
  .prefectureSelect {
    position: static;
    flex-shrink: 0;

    @include pc {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
    }
  }
}

.commonButton {
  width: 47rem;
  margin-left: auto;
  margin-right: auto;
}

.commonMargin {
  margin-left: $spCommonMargin;
  margin-right: $spCommonMargin;
}
</style>
